import Image from "next/image";
import { ArrowUp, Check, ChevronDown, Copy, Mic, Plus, SlidersHorizontal, ThumbsDown, ThumbsUp } from "lucide-react";
import { TwinMark } from "@product/components/app/twin/twin-mark";
import { answerSources, answerText, type StoryAnswer } from "./answer-content";
import styles from "./external-chat.module.css";

type Provider = "chatgpt" | "claude" | "gemini" | "copilot";
const LABELS = { chatgpt: "ChatGPT", claude: "Claude", gemini: "Gemini", copilot: "Copilot" } as const;
/** What each client's composer invites, and the control beside its tools. */
const COMPOSER = {
  chatgpt: { placeholder: "Ask anything", control: "Tools" },
  claude: { placeholder: "Reply to Claude…", control: "Choose model" },
  gemini: { placeholder: "Ask Gemini", control: "Tools" },
  copilot: { placeholder: "Message Copilot", control: "Work" },
} as const;

export function AssistantMark({ provider }: { provider: Provider }) {
  if (provider === "copilot") return <span className={styles.copilotMark} aria-hidden="true" />;
  return <Image src={`/concepts/source-icons/${provider === "chatgpt" ? "chatgpt-mark" : provider}.svg`} width={20} height={20} alt="" />;
}

/**
 * Branded illustrative clients, each a derivation of the Twin's own chat
 * (product-shot/twin-chat-card): the same question, the same sentence and
 * the same systems behind it, by their own marks, in the client's own look
 * (its bubble, type, composer and colours), so a reader can tell which
 * client each is and that the answer is the Twin's in all of them.
 */
export function ExternalChat({ provider, answer, dense = false }: { provider: Provider; answer: StoryAnswer; dense?: boolean }) {
  return <div className={styles.chat} data-provider={provider} data-dense={dense}>
    <div className={styles.transcript}>
      <div className={styles.prompt}>{answer.question}</div>
      <div className={styles.response}>
        <div className={styles.assistant}><AssistantMark provider={provider} /><strong>{LABELS[provider]}</strong></div>
        <div className={styles.tool}><TwinMark /><span>{provider === "copilot" ? "Your Twin agent" : "Used your Twin"}</span><Check size={12} aria-hidden="true" /></div>
        <p className={styles.answer}>{answerText(answer)}</p>
        <ul className={styles.sources} aria-label="Answer sources">{answerSources(answer).map(source => <li key={source.slug}><Image src={source.iconUrl} width={14} height={14} alt="" /><strong>{source.label}</strong></li>)}</ul>
        <div className={styles.responseActions} aria-hidden="true"><Copy /><ThumbsUp /><ThumbsDown /></div>
      </div>
    </div>
    <div className={styles.composer} aria-hidden="true">
      <span>{COMPOSER[provider].placeholder}</span>
      <div><Plus /><SlidersHorizontal /><span>{COMPOSER[provider].control}<ChevronDown /></span><Mic /><i><ArrowUp /></i></div>
    </div>
  </div>;
}
